/**
 * A readable name for an LLM model id — "claude-opus-5-5" → "Claude Opus 5.5".
 *
 * Model menus are now the vendors' LIVE lists (the AI gateway's
 * `model_catalog`), so a new release shows up without a deploy. A hand-kept
 * id → label map would lag behind exactly those releases and print the raw
 * id for every new one, so the name is derived from the id's own shape
 * instead. An id no rule recognises is returned unchanged.
 */
export function modelDisplayName(id: string | null | undefined): string {
  const raw = (id ?? "").trim();
  const m = raw.toLowerCase();
  if (!m) return "";

  if (m.startsWith("claude-")) {
    // claude-<family…>-<major>-<minor>[-YYYYMMDD]
    const parts = m.slice("claude-".length).replace(/-\d{8}$/, "").split("-");
    const words: string[] = [];
    const nums: string[] = [];
    for (const p of parts) (/^\d+$/.test(p) ? nums : words).push(p);
    const family = words.map(cap).join(" ");
    return ["Claude", family, nums.join(".")].filter(Boolean).join(" ");
  }

  if (m.startsWith("gpt-")) {
    const chatLatest = m.endsWith("-chat-latest");
    const rest = m.slice("gpt-".length).replace(/-chat-latest$/, "").replace(/-\d{4}-\d{2}-\d{2}$/, "");
    const [version = "", ...tail] = rest.split("-");
    const name = [`GPT-${version}`, ...tail.map(cap)].join(" ");
    return chatLatest ? `${name} Chat (latest)` : name;
  }

  if (m.startsWith("deepseek-")) {
    const tail = m.slice("deepseek-".length).split("-").map((p) => (/^v\d/.test(p) ? p.toUpperCase() : cap(p)));
    return ["DeepSeek", ...tail].join(" ");
  }

  return raw;
}

function cap(word: string): string {
  return word ? word[0]!.toUpperCase() + word.slice(1) : word;
}
